import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import './Auth.css';

const passwordRules = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One number', test: (p) => /[0-9]/.test(p) },
  { label: 'One special character (!@#$%...)', test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(p) },
];

const ResetPassword = () => {
  const { token } = useParams();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const ruleResults = useMemo(() =>
    passwordRules.map(rule => ({ ...rule, passed: rule.test(password) })),
    [password]
  );

  const allRulesPassed = ruleResults.every(r => r.passed);
  const passedCount = ruleResults.filter(r => r.passed).length;

  const strengthLabel = passedCount === 0 ? '' : passedCount <= 1 ? 'Weak' : passedCount <= 2 ? 'Fair' : passedCount <= 3 ? 'Good' : 'Strong';
  const strengthColor = passedCount <= 1 ? '#ef4444' : passedCount <= 2 ? '#f59e0b' : passedCount <= 3 ? '#3b82f6' : '#22c55e';

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const res = await authAPI.verifyResetToken(token);
      setTokenValid(true);
      setUserEmail(res.data.email);
    } catch (err) {
      setTokenValid(false);
      setError('This reset link is invalid or has expired.');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allRulesPassed) {
      setError('Please meet all password requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(token, { password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="auth-container">
        <div className="auth-background">
          <div className="auth-orb auth-orb-1"></div>
          <div className="auth-orb auth-orb-2"></div>
          <div className="auth-orb auth-orb-3"></div>
        </div>
        <div className="auth-card">
          <div className="auth-form" style={{ textAlign: 'center' }}>
            <div className="spinner-large"></div>
            <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: '16px' }}>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-orb auth-orb-1"></div>
        <div className="auth-orb auth-orb-2"></div>
        <div className="auth-orb auth-orb-3"></div>
      </div>

      <div className="auth-card">
        <div className="auth-logo">
          <img src="/logo.svg" alt="EchoMemoir" style={{ width: '80px', height: '80px' }} />
          <h1>EchoMemoir</h1>
          <p>Preserve voices and stories for generations</p>
        </div>

        {success ? (
          <div className="auth-form">
            <h2>Password Reset! ✅</h2>
            <div className="auth-success">
              <p>Your password has been updated successfully. You can now sign in with your new password.</p>
            </div>
            <Link to="/login" className="auth-button" style={{ textDecoration: 'none', textAlign: 'center' }}>
              Sign In
            </Link>
          </div>
        ) : !tokenValid ? (
          <div className="auth-form">
            <h2>Link Expired</h2>
            <div className="auth-error">{error}</div>
            <Link to="/forgot-password" className="auth-button" style={{ textDecoration: 'none', textAlign: 'center' }}>
              Request New Reset Link
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <h2>Reset Password</h2>
            <p className="auth-subtitle">Enter a new password for <strong>{userEmail}</strong></p>

            {error && <div className="auth-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>

              {password.length > 0 && (
                <div className="password-strength">
                  <div className="strength-bar">
                    <div
                      className="strength-fill"
                      style={{
                        width: `${(passedCount / passwordRules.length) * 100}%`,
                        backgroundColor: strengthColor
                      }}
                    ></div>
                  </div>
                  <span className="strength-label" style={{ color: strengthColor }}>
                    {strengthLabel}
                  </span>
                </div>
              )}

              {password.length > 0 && (
                <ul className="password-rules">
                  {ruleResults.map((rule, i) => (
                    <li key={i} className={rule.passed ? 'passed' : 'failed'}>
                      <span className="rule-icon">{rule.passed ? '✔' : '○'}</span>
                      {rule.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {confirmPassword.length > 0 && password !== confirmPassword && (
                <p className="field-error">Passwords do not match</p>
              )}
              {confirmPassword.length > 0 && password === confirmPassword && (
                <p className="field-success">Passwords match ✔</p>
              )}
            </div>

            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? <span className="spinner"></span> : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;